/**
 * Feature flags from the API (PHASE-3 §3: feature gates come from entitlements, plan, trial and permissions).
 *
 * One call: `GET /me` → `currentTenant` (REQUESTS #36, API 0.15.0) carries the member's `permissions` (11 §3), the
 * workspace's `planCode`, `status`, `readOnlyReason`, `trialEndsAt`, `usersLimit` and the current plan's `features`
 * and `limits` (as in `GET /billing/plans`; never hard-coded).
 *
 * The UI only hides or disables; the server enforces every rule. Fail closed: while `/me` is loading, or when the
 * plan row is missing (`features: null`), no feature is on and nothing is writable.
 */
import { useGetMe } from "@brevalt/api-client/customer";
import type { BillingPlanFeatures, BillingPlanLimits, CurrentTenant, Me } from "@brevalt/api-client/customer/model";
import { parseInstant } from "./time";

/** Tenant states in which changes outside the legal allow-list are refused (403 workspace-read-only). */
export const READ_ONLY_STATUSES = ["read_only", "suspended", "archived", "deleting", "deleted"] as const;

type BooleanFeature = {
  [K in keyof BillingPlanFeatures]-?: BillingPlanFeatures[K] extends boolean ? K : never;
}[keyof BillingPlanFeatures];

export interface Entitlements {
  permissions: ReadonlySet<string>;
  /** The current workspace (null: none, see `GET /me` → `tenantProblem`). */
  tenant: CurrentTenant | null;
  planCode: string | null;
  tenantStatus: string | null;
  readOnlyReason: string | null;
  trialEndsAt: string | null;
  /** When the workspace becomes (or became) read-only after a failed payment (API 0.16.0, REQUESTS #51b). */
  readOnlyFrom: string | null;
  /** Effective users limit (-1 unlimited); null unless the member has `member.manage` or `billing.manage`. */
  usersLimit: number | null;
  features: BillingPlanFeatures | null;
  limits: BillingPlanLimits | null;
  /** True only with a known plan whose `writable` is true, outside the read-only states. */
  writable: boolean;
  /**
   * The demo installation (`GET /me` → `installation.demo`, ADR-039): every feature is on and nothing is sold, so the
   * app shows no upgrade prompt, plan lock, trial notice or checkout, and a "Demo" badge instead.
   */
  demo: boolean;
  /**
   * The browser extension is published (`GET /me` → `installation.features.srpExtension`, owner decision 0.1.10,
   * contract 0.33.0): until then it is never mentioned or offered.
   */
  srpExtension: boolean;
  can: (permission: string) => boolean;
  /** A boolean plan feature (`fourEyes`, `customRoles`, `api`, …); false until `/me` is loaded. */
  has: (feature: BooleanFeature) => boolean;
}

export function isReadOnlyStatus(status: string | null | undefined): boolean {
  return status != null && (READ_ONLY_STATUSES as readonly string[]).includes(status);
}

/** `installation.features.srpExtension` of `/me` (contract 0.33.0): false or no `/me` = not published. */
export function srpExtensionPublished(me?: Me | null): boolean {
  return me?.installation.features.srpExtension === true;
}

export function computeEntitlements(me?: Me | null): Entitlements {
  const tenant = me?.currentTenant ?? null;
  const permissions = new Set(tenant?.permissions ?? []);
  const features = tenant?.features ?? null;
  return {
    permissions,
    tenant,
    planCode: tenant?.planCode ?? null,
    tenantStatus: tenant?.status ?? null,
    readOnlyReason: tenant?.readOnlyReason ?? null,
    trialEndsAt: tenant?.trialEndsAt ?? null,
    readOnlyFrom: tenant?.readOnlyFrom ?? null,
    usersLimit: tenant?.usersLimit ?? null,
    features,
    limits: tenant?.limits ?? null,
    writable: features?.writable === true && !isReadOnlyStatus(tenant?.status),
    demo: me?.installation.demo === true,
    srpExtension: srpExtensionPublished(me),
    can: (p) => permissions.has(p),
    has: (f) => features?.[f] === true,
  };
}

/** Entitlements of the signed-in member in the current workspace. */
export function useEntitlements(): Entitlements & { isLoading: boolean } {
  const me = useGetMe();
  return { ...computeEntitlements(me.data), isLoading: me.isPending };
}

/** Whole days left in the trial (rounded up; 0 once it ended), or null without an end date. */
export function trialDaysLeft(trialEndsAt: string | null | undefined, now: Date): number | null {
  if (!trialEndsAt) return null;
  const ms = parseInstant(trialEndsAt).getTime() - now.getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86_400_000));
}
